import React from "react";
import { MapPin, Mail, Phone, Clock, AlertCircle, CalendarDays } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useConversion } from "@/context/ConversionContext";
import { useSeo } from "@/lib/api";
import { site, isMissing, whatsappLink } from "@/config/site";
import { Section, Reveal, Eyebrow } from "@/components/common/Section";
import Breadcrumb from "@/components/common/Breadcrumb";

export default function Contact() {
  const { t, lang } = useLang();
  const { openForm, openCalendly } = useConversion();
  useSeo({ lang, path: "/contact", title: `${t.nav.contact.label} — NEKASSUR`, description: t.contact.intro });

  const Missing = () => (
    <span className="inline-flex items-center gap-1 rounded-md bg-nek-coral/15 px-2 py-0.5 text-xs font-semibold text-nek-coral">
      <AlertCircle className="h-3 w-3" /> {t.contact.toFill}
    </span>
  );

  const wLink = whatsappLink(t.whatsapp.prefill);

  return (
    <>
      <section className="bg-nek-navy" data-hide-appointment>
        <div className="nek-container py-12 sm:py-16">
          <Breadcrumb onDark items={[{ label: t.nav.contact.label }]} />
          <Reveal className="mt-8 max-w-3xl">
            <Eyebrow className="!text-nek-teal-100">{t.contact.eyebrow}</Eyebrow>
            <h1 className="nek-h1 mt-4 text-white" style={{ fontSize: "clamp(2.2rem,4vw,3.4rem)" }}>{t.contact.title}</h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-white/70">{t.contact.intro}</p>
          </Reveal>
        </div>
      </section>

      <Section tone="white" data-hide-appointment>
        <div className="grid gap-10 lg:grid-cols-2">
          <Reveal>
            <div className="rounded-[24px] border border-nek-border bg-nek-light p-8">
              <h2 className="text-xl font-heading font-bold text-nek-navy">{t.contact.infoTitle}</h2>
              <ul className="mt-6 space-y-5">
                <li className="flex items-start gap-3">
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-nek-blue"><MapPin className="h-5 w-5" /></span>
                  <div><p className="text-sm font-semibold text-nek-navy">{t.contact.addressLabel}</p><p className="text-sm text-nek-gray">{site.address}</p></div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-nek-blue"><Phone className="h-5 w-5" /></span>
                  <div><p className="text-sm font-semibold text-nek-navy">{t.contact.phoneLabel}</p>{isMissing(site.phone) ? <Missing /> : <a href={`tel:${site.phone}`} className="text-sm text-nek-blue">{site.phone}</a>}</div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-nek-blue"><Mail className="h-5 w-5" /></span>
                  <div><p className="text-sm font-semibold text-nek-navy">{t.contact.emailLabel}</p>{isMissing(site.contactEmail) ? <Missing /> : <a href={`mailto:${site.contactEmail}`} className="text-sm text-nek-blue">{site.contactEmail}</a>}</div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-nek-blue"><Clock className="h-5 w-5" /></span>
                  <div><p className="text-sm font-semibold text-nek-navy">{t.contact.hoursLabel}</p><p className="text-sm text-nek-gray">{t.contact.hours}</p></div>
                </li>
              </ul>
            </div>
          </Reveal>

          <Reveal>
            <div className="rounded-[24px] bg-nek-navy p-8 text-white">
              <h2 className="text-xl font-heading font-bold text-white">{t.form.title}</h2>
              <p className="mt-3 text-white/70">{t.contact.orBook}</p>
              <div className="mt-6 flex flex-col gap-3">
                <button type="button" onClick={() => openForm()} data-testid="contact-find-solution" className="btn-coral w-full">{t.cta.findSolution}</button>
                <button type="button" onClick={openCalendly} data-testid="contact-book" className="btn-outline w-full !bg-transparent !text-white !border-white/30 hover:!border-white"><CalendarDays className="h-4 w-4" /> {t.cta.bookAppointment}</button>
                {site.whatsappNumber && wLink && (
                  <a href={wLink} target="_blank" rel="noopener noreferrer" className="btn-outline w-full !bg-transparent !text-white !border-white/30 hover:!border-white">WhatsApp</a>
                )}
              </div>
            </div>
          </Reveal>
        </div>
      </Section>
    </>
  );
}
